'use client';

import { useState, useEffect } from 'react';
import { Popup } from './index';
import { PopupItem } from '@/types/popup';
import { getApiUrl } from '@/utils';

/**
 * PopupManager 컴포넌트 Props 인터페이스
 * 
 * 팝업 관리자에 필요한 팝업 데이터를 정의합니다.
 */
interface PopupManagerProps {
  popups?: PopupItem[]; // 선택적 prop으로 변경
}

/**
 * 팝업 관리자 컴포넌트
 * 
 * 홈페이지에 표시될 팝업들을 관리하는 컴포넌트입니다.
 * "오늘 하루 보지 않기" 기능을 통해 사용자 경험을 개선하며,
 * 여러 팝업 간의 순차적 표시를 제어합니다.
 * 
 * 주요 기능:
 * - 팝업 자동 표시 (페이지 로드 시)
 * - 오늘 하루 보지 않기 설정 (localStorage 기반)
 * - 여러 팝업 간 좌우 이동
 * - 팝업 상태 관리 (열기/닫기)
 * - 팝업이 없을 때 렌더링 방지
 */
export function PopupManager({ popups }: PopupManagerProps) {
  const [popupData, setPopupData] = useState<PopupItem[]>(popups || []);
  const [loading, setLoading] = useState(!popups); // popups prop이 없을 경우 로딩 상태

  // popups prop이 없을 경우 API에서 데이터를 가져옴
  useEffect(() => {
    if (!popups) {
      const fetchPopups = async () => {
        try {
          setLoading(true);
          const response = await fetch(getApiUrl('popups'));

          if (response.ok) {
            const result = await response.json();
            if (result.success) {
              setPopupData(result.data || []);
            }
          }
        } catch (error) {
          console.error('Failed to fetch popups:', error);
          setPopupData([]); // 에러 발생 시 빈 배열로 설정
        } finally {
          setLoading(false);
        }
      };

      fetchPopups();
    }
  }, [popups]);

  // 오늘 날짜와 마지막 "보지 않기" 설정 날짜 비교
  const today = new Date().toDateString();
  const lastDontShowDate = typeof window !== 'undefined' ? localStorage.getItem('popupDontShowDate') : null;
  const shouldShowPopup = lastDontShowDate !== today;

  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [currentPopupIndex, setCurrentPopupIndex] = useState(0);

  /**
   * 팝업 자동 표시 설정
   * 
   * 페이지 로드 시 "오늘 하루 보지 않기" 설정이 없고
   * 팝업이 있는 경우 자동으로 팝업을 표시합니다.
   */
  useEffect(() => {
    if (shouldShowPopup && popupData.length > 0 && !loading) {
      setCurrentPopupIndex(0);
      setIsPopupOpen(true);
    }
  }, [shouldShowPopup, popupData, loading]);

  /**
   * 팝업 닫기 핸들러
   * 
   * 팝업을 닫고 상태를 업데이트합니다.
   */
  const handleClose = () => {
    setIsPopupOpen(false);
  };

  /**
   * 오늘 하루 보지 않기 핸들러
   * 
   * localStorage에 오늘 날짜를 저장하여
   * 오늘 하루 동안 팝업이 표시되지 않도록 합니다.
   */
  const handleDontShowToday = () => {
    const today = new Date().toDateString();
    localStorage.setItem('popupDontShowDate', today);
  };

  /**
   * 다음 팝업으로 이동 핸들러
   * 
   * 마지막 팝업이 아닌 경우에만 다음 팝업으로 이동합니다.
   */
  const handleNext = () => {
    if (currentPopupIndex < popupData.length - 1) {
      setCurrentPopupIndex(currentPopupIndex + 1);
    }
  };

  /**
   * 이전 팝업으로 이동 핸들러
   * 
   * 첫 번째 팝업이 아닌 경우에만 이전 팝업으로 이동합니다.
   */
  const handlePrev = () => {
    if (currentPopupIndex > 0) {
      setCurrentPopupIndex(currentPopupIndex - 1);
    }
  };

  // 로딩 중이거나 팝업이 없으면 렌더링하지 않음
  if (loading || popupData.length === 0) {
    return null;
  }

  // 팝업 컴포넌트 렌더링
  return (
    <Popup
      isOpen={isPopupOpen}
      onClose={handleClose}
      onDontShowToday={handleDontShowToday}
      popups={popupData}
      currentIndex={currentPopupIndex}
      onNext={handleNext}
      onPrev={handlePrev}
    />
  );
}